import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import { useSessionIndicators } from "@shared-core/domain/useSessionIndicators";
import { Button } from "../app/components/primitives/button";
import { Badge } from "../app/components/primitives/badge";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "../app/components/primitives/resizable";
import { ChatPanel } from "../app/components/domain/feature-sessions/ChatPanel";
import { RemoteControlPanel } from "../app/components/domain/feature-sessions/RemoteControlPanel";
import { PreviewPanel } from "../app/components/domain/feature-sessions/PreviewPanel";
import { SessionIndicators } from "../app/components/domain/feature-sessions/SessionIndicators";
import { OpenPRDialog } from "../app/components/domain/feature-sessions/OpenPRDialog";
import { SharePreviewDialog } from "../app/components/domain/feature-sessions/SharePreviewDialog";
import {
  GitBranch,
  Play,
  Square,
  RotateCw,
  Save,
  ExternalLink,
  ArrowLeft,
  User,
  Eye,
  MessageSquare,
  Clock,
  GitPullRequest,
  Share2,
} from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { getDefaultRepository } from "@shared-core/data/feature-session-types";
import type {
  FeatureBranch,
  SessionResponse,
  PreviewStatus,
} from "@shared-core/data/feature-session-types";
import { paths } from "@shared-core/routing/routes";
import { useSmartBack } from "../app/useSmartBack";

export function FeatureSessionWorkspacePage() {
  const { branchName: encodedBranch } = useParams<{ branchName: string }>();
  const [searchParams] = useSearchParams();
  const goBack = useSmartBack(paths.featureSessions());
  const dataAdapter = useDataAdapter();

  const branchName = (encodedBranch ?? "").split("~").join("/");
  const repository = searchParams.get("repo") ?? getDefaultRepository();
  // Teams-mode hides the explicit Save button — durability comes from
  // per-turn auto-commit+push, so the user doesn't need to remember.
  // Save endpoint itself remains available for UI-mode and external callers.
  const teamsMode = searchParams.get("mode") === "teams";

  const [feature, setFeature] = useState<FeatureBranch | null>(null);
  const [, setSession] = useState<SessionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewStatus | null>(null);
  // One-time exchange URL for the preview iframe. Fetched once when a chat-tier
  // session first becomes active; cleared on session end or manual refresh so a
  // fresh JWT is minted on the next load.
  const [exchangeUrl, setExchangeUrl] = useState<string | null>(null);

  // PR and share dialog state (Phase 3)
  const [prDialogOpen, setPrDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const isActive = !!feature?.active_environment_id;
  // Remote Control sessions are driven from claude.ai/code, not from an in-app
  // chat + preview. The session/indicators and session/preview surfaces are
  // chat-tier concepts and 500/502 for an RC task, so we neither poll nor
  // render them here — see RemoteControlPanel for the RC view.
  const isRemoteControl = feature?.session_kind === "remote_control";

  const {
    indicators,
    loading: indicatorsLoading,
    refresh: refreshIndicators,
  } = useSessionIndicators({
    repository,
    branchName,
    enabled: isActive && !isRemoteControl,
  });

  // Load feature detail
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const result = await dataAdapter.getFeatureDetail(
          repository,
          branchName,
        );
        setFeature(result);
      } catch (error) {
        console.error("Error loading feature:", error);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [dataAdapter, repository, branchName]);

  // Poll session state so isActive stays accurate without requiring a full
  // page reload. Without this, a session started externally (e.g. via Teams)
  // or one that stops while the user is on the page won't update the view.
  useEffect(() => {
    const id = setInterval(async () => {
      if (actionLoading) return; // don't clobber in-flight actions
      try {
        const updated = await dataAdapter.getFeatureDetail(
          repository,
          branchName,
        );
        setFeature(updated);
      } catch {
        // silently ignore — stale state is better than an error flash
      }
    }, 10_000);
    return () => clearInterval(id);
  }, [dataAdapter, repository, branchName, actionLoading]);

  // Poll preview health when a chat-tier session is active. RC sessions have no
  // in-app preview, so skip (the endpoint 500s for an RC task).
  useEffect(() => {
    if (!isActive || isRemoteControl) {
      setPreview(null);
      return;
    }
    async function fetchPreview() {
      try {
        const result = await dataAdapter.getSessionPreview(
          repository,
          branchName,
        );
        setPreview(result);
      } catch {
        // Silently ignore preview fetch errors
      }
    }
    fetchPreview();
    const id = setInterval(fetchPreview, 5000);
    return () => clearInterval(id);
  }, [dataAdapter, repository, branchName, isActive, isRemoteControl]);

  // Clear exchange URL when the session ends so a fresh JWT is minted on restart.
  useEffect(() => {
    if (!isActive || isRemoteControl) {
      setExchangeUrl(null);
    }
  }, [isActive, isRemoteControl]);

  // Fetch a one-time exchange URL as soon as a chat-tier session is active.
  // The URL is used once by the preview iframe to bootstrap the auth cookie flow;
  // after that the browser's cookies carry auth and this URL is no longer needed.
  useEffect(() => {
    if (!isActive || isRemoteControl || exchangeUrl) return;
    let cancelled = false;
    dataAdapter
      .getPreviewExchangeUrl(repository, branchName)
      .then((result) => {
        if (!cancelled) setExchangeUrl(result.exchange_url);
      })
      .catch(() => {
        // Preview may not be configured; silently suppress
      });
    return () => {
      cancelled = true;
    };
  }, [
    dataAdapter,
    repository,
    branchName,
    isActive,
    isRemoteControl,
    exchangeUrl,
  ]);

  const refreshPreview = useCallback(async () => {
    try {
      const result = await dataAdapter.getSessionPreview(
        repository,
        branchName,
      );
      setPreview(result);
    } catch {
      // ignore
    }
    // Clear exchange URL so a fresh JWT is minted and the iframe fully reloads.
    setExchangeUrl(null);
  }, [dataAdapter, repository, branchName]);

  async function handleStart() {
    setActionLoading("start");
    const toastId = toast.loading("Building container...");

    // Poll provisioning stage while the blocking start request is in flight,
    // updating the toast so the user knows what's happening during the ~2-4 min wait.
    const pollId = setInterval(async () => {
      try {
        const status = await dataAdapter.getProvisioningStatus(
          repository,
          branchName,
        );
        if (status.label) {
          toast.loading(status.label, { id: toastId });
        }
      } catch {
        // Silently ignore — polling errors don't affect the start request.
      }
    }, 3000);

    try {
      const result = await dataAdapter.startSession(repository, branchName);
      setSession(result);
      const updated = await dataAdapter.getFeatureDetail(
        repository,
        branchName,
      );
      setFeature(updated);
      toast.success("Session ready!", { id: toastId });
    } catch (error) {
      console.error("Error starting session:", error);
      // 409 means a session is already running — refresh and show the active view
      // rather than a generic failure toast.
      if (error instanceof ApiError && error.status === 409) {
        try {
          const updated = await dataAdapter.getFeatureDetail(
            repository,
            branchName,
          );
          setFeature(updated);
          toast.dismiss(toastId);
          toast.info("Session is already running");
        } catch {
          toast.error("Failed to start session", { id: toastId });
        }
      } else {
        toast.error("Failed to start session", { id: toastId });
      }
    } finally {
      clearInterval(pollId);
      setActionLoading(null);
    }
  }

  async function handleStop() {
    setActionLoading("stop");
    try {
      await dataAdapter.stopSession(repository, branchName);
      setSession(null);
      const updated = await dataAdapter.getFeatureDetail(
        repository,
        branchName,
      );
      setFeature(updated);
      toast.success("Session stopped");
    } catch (error) {
      console.error("Error stopping session:", error);
      toast.error("Failed to stop session");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleResume() {
    setActionLoading("resume");
    try {
      const result = await dataAdapter.resumeSession(repository, branchName);
      setSession(result);
      const updated = await dataAdapter.getFeatureDetail(
        repository,
        branchName,
      );
      setFeature(updated);
      toast.success("Session resumed");
    } catch (error) {
      console.error("Error resuming session:", error);
      toast.error("Failed to resume session");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleSave() {
    setActionLoading("save");
    try {
      const result = await dataAdapter.saveSession(repository, branchName);
      toast.success(`Progress saved — commit ${result.commit_sha}`);
      refreshIndicators();
    } catch (error) {
      console.error("Error saving session:", error);
      toast.error("Failed to save progress");
    } finally {
      setActionLoading(null);
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <h1 className="text-xl font-bold mb-4">Feature workspace</h1>
        Loading feature...
      </div>
    );
  }

  if (!feature) {
    return (
      <div className="space-y-4 py-12 text-center">
        <h1 className="text-xl font-bold">Feature not found</h1>
        <Button variant="outline" onClick={goBack}>
          <ArrowLeft className="size-4 mr-2" />
          Back to Features
        </Button>
      </div>
    );
  }

  // Active session → split-pane workspace
  if (isActive) {
    return (
      <div className="flex flex-col" style={{ height: "calc(100vh - 80px)" }}>
        {/* Compact header bar */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-surface">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={goBack}
            >
              <ArrowLeft className="size-4" />
            </Button>
            <GitBranch className="size-4 text-muted-foreground flex-shrink-0" />
            <span className="font-semibold text-sm truncate">
              {feature.branch_name}
            </span>
            <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
              <span className="inline-block size-1.5 rounded-full bg-green-500 mr-1" />
              Active
            </Badge>
            {feature.last_known_pr_url && (
              <a
                href={feature.last_known_pr_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <ExternalLink className="size-3" />
                PR
              </a>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPrDialogOpen(true)}
              disabled={!!actionLoading}
            >
              <GitPullRequest className="size-3.5 mr-1" />
              Open PR
            </Button>
            {/* Share (preview-based) and Save (per-turn commit) are chat-tier;
                an RC session is saved/shared from claude.ai/code. */}
            {!isRemoteControl && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShareDialogOpen(true)}
                disabled={!!actionLoading || !preview?.healthy}
              >
                <Share2 className="size-3.5 mr-1" />
                Share
              </Button>
            )}
            {!isRemoteControl && !teamsMode && (
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!!actionLoading}
                data-testid="feature-save-button"
              >
                <Save className="size-3.5 mr-1" />
                {actionLoading === "save" ? "Saving..." : "Save"}
              </Button>
            )}
            <Button
              size="sm"
              variant="destructive"
              onClick={handleStop}
              disabled={!!actionLoading}
            >
              <Square className="size-3.5 mr-1" />
              {actionLoading === "stop" ? "Stopping..." : "Stop"}
            </Button>
          </div>
        </div>

        {isRemoteControl ? (
          // RC: a single full-width panel. The session is driven from
          // claude.ai/code, so there is no in-app chat, indicators, or preview.
          <div className="flex-1 min-h-0">
            <RemoteControlPanel
              enabled={isActive}
              repository={repository}
              branchName={branchName}
            />
          </div>
        ) : (
          <>
            {/* Session indicators */}
            <SessionIndicators
              indicators={indicators}
              loading={indicatorsLoading}
            />

            {/* Split pane: chat + preview */}
            <ResizablePanelGroup orientation="horizontal" className="flex-1">
              <ResizablePanel defaultSize={50} minSize={25}>
                <ChatPanel
                  enabled={isActive}
                  repository={repository}
                  branchName={branchName}
                />
              </ResizablePanel>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={50} minSize={25}>
                <PreviewPanel
                  exchangeUrl={exchangeUrl}
                  directUrl={preview?.preview_url || null}
                  healthy={preview?.healthy ?? false}
                  onRefresh={refreshPreview}
                />
              </ResizablePanel>
            </ResizablePanelGroup>
          </>
        )}

        {/* PR and share dialogs */}
        <OpenPRDialog
          open={prDialogOpen}
          onClose={() => setPrDialogOpen(false)}
          repository={repository}
          branchName={branchName}
          onSuccess={(pr) => {
            setFeature((prev) =>
              prev ? { ...prev, last_known_pr_url: pr.url } : prev,
            );
          }}
        />
        <SharePreviewDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          repository={repository}
          branchName={branchName}
        />
      </div>
    );
  }

  // Idle state → detail view
  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={goBack}
        className="text-muted-foreground"
      >
        <ArrowLeft className="size-4 mr-1" />
        Features
      </Button>

      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <GitBranch className="size-5 text-muted-foreground" />
            <h1 className="text-xl font-bold">{feature.branch_name}</h1>
            <Badge variant="secondary">
              <span className="inline-block size-1.5 rounded-full bg-gray-400 mr-1.5" />
              Idle
            </Badge>
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleStart} disabled={!!actionLoading}>
              <Play className="size-4 mr-1" />
              {actionLoading === "start" ? "Starting..." : "Start Session"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleResume}
              disabled={!!actionLoading}
            >
              <RotateCw className="size-4 mr-1" />
              {actionLoading === "resume" ? "Resuming..." : "Resume"}
            </Button>
          </div>
        </div>

        {feature.last_known_pr_url && (
          <a
            href={feature.last_known_pr_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <ExternalLink className="size-3" />
            Pull Request
          </a>
        )}
      </div>

      <div className="rounded-lg border border-border bg-surface p-4 space-y-3">
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <User className="size-4" />
            Owner: {feature.owner_user_id}
          </span>
          <span className="flex items-center gap-1">
            <Eye className="size-4" />
            Visibility: {feature.visibility_scope}
          </span>
          {feature.feedback_ids.length > 0 && (
            <span className="flex items-center gap-1">
              <MessageSquare className="size-4" />
              {feature.feedback_ids.length} linked feedback
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock className="size-4" />
            Created{" "}
            {formatDistanceToNow(new Date(feature.created_at), {
              addSuffix: true,
            })}
          </span>
        </div>
      </div>
    </div>
  );
}
